import { forwardRef } from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cn } from '../../lib/cn.js'

const VARIANTS = {
  primary:
    'bg-[var(--color-brand)] text-[var(--color-brand-foreground)] hover:opacity-90 focus-visible:ring-[var(--color-brand)]',
  secondary:
    'bg-[var(--color-muted)] text-[var(--color-surface-foreground)] hover:bg-[var(--color-muted)]/80 focus-visible:ring-[var(--color-muted-foreground)]',
  ghost:
    'bg-transparent text-[var(--color-surface-foreground)] hover:bg-[var(--color-muted)] focus-visible:ring-[var(--color-muted-foreground)]',
}

const SIZES = {
  sm: 'h-8 px-3 text-sm',
  md: 'h-10 px-4 text-sm',
  lg: 'h-12 px-5 text-base',
  icon: 'h-10 w-10',
}

/**
 * Headless-ish button. Backs all chat surfaces - send-message,
 * close-panel, lead form CTAs - and falls through to a Radix `Slot`
 * when callers pass `asChild` so Tooltip/Dialog triggers don't end
 * up nesting `<button>` inside `<button>`.
 */
const Button = forwardRef(
  (
    {
      className,
      variant = 'primary',
      size = 'md',
      asChild = false,
      type = 'button',
      ...props
    },
    ref,
  ) => {
    const Component = asChild ? Slot : 'button'
    return (
      <Component
        ref={ref}
        type={asChild ? undefined : type}
        className={cn(
          'inline-flex items-center justify-center gap-2 rounded-full font-medium',
          'transition-colors disabled:pointer-events-none disabled:opacity-60',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
          VARIANTS[variant] ?? VARIANTS.primary,
          SIZES[size] ?? SIZES.md,
          className,
        )}
        {...props}
      />
    )
  },
)
Button.displayName = 'Button'

export { Button }
